/**
 * Family Member Storage Service
 *
 * Stores family members in localStorage for the Family Medical Financial Planner.
 * Follows the same singleton pattern as scenarioStorage.ts.
 * No authentication required - everything is stored locally.
 */

import { v4 as uuidv4 } from 'uuid';
import { scenarioStorage, LocalScenario } from './scenarioStorage';

export type FamilyRelationship = 'self' | 'spouse' | 'child' | 'parent' | 'sibling' | 'other';

export interface FamilyMember {
  id: string;
  name: string;
  relationship: FamilyRelationship;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

class FamilyStorageService {
  private readonly STORAGE_KEYS = {
    FAMILY_MEMBERS: 'luigis_family_members',
    FAMILY_INITIALIZED: 'luigis_family_initialized',
  };

  /**
   * Ensure "Self" member exists on first access
   */
  private ensureSelfExists(): void {
    const initialized = localStorage.getItem(this.STORAGE_KEYS.FAMILY_INITIALIZED);
    if (initialized) return;

    const members = this.getRawMembers();
    const hasSelf = members.some(m => m.relationship === 'self');

    if (!hasSelf) {
      const selfMember: FamilyMember = {
        id: uuidv4(),
        name: 'Self',
        relationship: 'self',
        displayOrder: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      members.unshift(selfMember);
      localStorage.setItem(this.STORAGE_KEYS.FAMILY_MEMBERS, JSON.stringify(members));
    }

    localStorage.setItem(this.STORAGE_KEYS.FAMILY_INITIALIZED, 'true');
  }

  /**
   * Get raw members without initializing (internal use)
   */
  private getRawMembers(): FamilyMember[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.FAMILY_MEMBERS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading family members:', error);
      return [];
    }
  }

  /**
   * Get all family members (ensures Self exists)
   */
  getMembers(): FamilyMember[] {
    this.ensureSelfExists();
    return this.getRawMembers().sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Get a member by ID
   */
  getMemberById(id: string): FamilyMember | undefined {
    return this.getMembers().find(m => m.id === id);
  }

  /**
   * Add a new family member
   */
  addMember(name: string, relationship: FamilyRelationship): FamilyMember {
    const members = this.getMembers();
    const maxOrder = members.reduce((max, m) => Math.max(max, m.displayOrder), 0);

    const newMember: FamilyMember = {
      id: uuidv4(),
      name: name.trim(),
      relationship,
      displayOrder: maxOrder + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    members.push(newMember);
    localStorage.setItem(this.STORAGE_KEYS.FAMILY_MEMBERS, JSON.stringify(members));
    return newMember;
  }

  /**
   * Update an existing family member
   */
  updateMember(id: string, updates: { name?: string; relationship?: FamilyRelationship }): FamilyMember | null {
    const members = this.getMembers();
    const index = members.findIndex(m => m.id === id);
    if (index === -1) return null;

    // Don't allow changing Self's relationship
    if (members[index].relationship === 'self' && updates.relationship && updates.relationship !== 'self') {
      return null;
    }

    members[index] = {
      ...members[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(this.STORAGE_KEYS.FAMILY_MEMBERS, JSON.stringify(members));
    return members[index];
  }

  /**
   * Remove a family member (refuses to delete Self)
   */
  removeMember(id: string): boolean {
    const members = this.getMembers();
    const member = members.find(m => m.id === id);

    // Cannot delete Self
    if (!member || member.relationship === 'self') return false;

    // Unassign any scenarios from this member
    const scenarios = scenarioStorage.getUserScenarios();
    scenarios.forEach(s => {
      if (s.familyMemberId === id) {
        scenarioStorage.assignToFamilyMember(s.id, null);
      }
    });

    const filtered = members.filter(m => m.id !== id);
    localStorage.setItem(this.STORAGE_KEYS.FAMILY_MEMBERS, JSON.stringify(filtered));
    return true;
  }

  /**
   * Get display name for a member
   */
  getDisplayName(member: FamilyMember): string {
    if (member.relationship === 'self') return 'Self';
    return member.name || `Person ${member.displayOrder}`;
  }

  /**
   * Get all scenarios assigned to a specific family member
   */
  getMemberScenarios(memberId: string): LocalScenario[] {
    return scenarioStorage.getUserScenarios().filter(s => s.familyMemberId === memberId);
  }

  /**
   * Get the Self member
   */
  getSelf(): FamilyMember {
    this.ensureSelfExists();
    const members = this.getRawMembers();
    return members.find(m => m.relationship === 'self')!;
  }
}

// Export singleton instance
export const familyStorage = new FamilyStorageService();
